/**
 * 🕐 Utilidades de Fecha - Normalización de Timezone del Servidor
 *
 * IMPORTANTE: El servidor guarda las fechas con 3 horas ADELANTADAS respecto a Chile.
 * Ejemplo: Si en Chile son las 17:30, el servidor guarda 20:30.
 * 
 * Para mostrar la hora correcta, debemos RESTAR 3 horas a las fechas del servidor.
 */

// Offset del servidor en horas (las fechas vienen 3 horas adelantadas)
const SERVER_OFFSET_HOURS = 3;

/**
 * Parsea una fecha del servidor de forma segura
 * El servidor puede enviar: "2026-01-22T10:30:00.000Z" o "2026-01-22 10:30:00"
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {Date|null} - Fecha parseada o null
 */
export const parseServerDate = (serverDate) => {
  if (!serverDate) return null;
  
  // Si ya es Date, retornar copia
  if (serverDate instanceof Date) {
    return isNaN(serverDate.getTime()) ? null : new Date(serverDate);
  }
  
  // Intentar parsear el string
  let date = new Date(serverDate);
  
  // Si falló, intentar formato alternativo
  if (isNaN(date.getTime())) {
    // Intentar formato "YYYY-MM-DD HH:mm:ss"
    const isoString = String(serverDate).replace(' ', 'T');
    date = new Date(isoString);
  }
  
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Normaliza una fecha del servidor restando el offset de 3 horas
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {Date|null} - Fecha normalizada
 */
export const normalizeServerDate = (serverDate) => {
  const date = parseServerDate(serverDate);
  if (!date) return null;
  
  // RESTAR 3 horas porque el servidor guarda fechas adelantadas
  date.setHours(date.getHours() - SERVER_OFFSET_HOURS);
  
  return date;
};

/**
 * Obtiene la fecha actual del cliente (navegador)
 * @returns {Date}
 */
export const getNow = () => new Date();

/**
 * Calcula el tiempo transcurrido desde una fecha del servidor (normalizada)
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {Object|null} - Objeto con días, horas, minutos y texto relativo
 */
export const getTimeElapsed = (serverDate) => {
  // Usar la fecha NORMALIZADA (con el offset aplicado)
  const date = normalizeServerDate(serverDate);
  if (!date) {
    console.warn("⚠️ getTimeElapsed: Fecha inválida", serverDate);
    return null;
  }

  const now = getNow();
  const diffMs = now.getTime() - date.getTime();

  // Si la diferencia es negativa (fecha futura), retornar valores mínimos
  if (diffMs < 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      totalHours: 0,
      totalMinutes: 0,
      relativeText: "Recién creada",
      isRecent: true,
    };
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = diffMs / (1000 * 60 * 60); // Mantener decimales para SLA
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  // Generar texto relativo
  let relativeText;
  if (days > 0) {
    relativeText = `Hace ${days} día${days > 1 ? "s" : ""}`;
  } else if (hours > 0) {
    relativeText = `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
  } else if (minutes > 0) {
    relativeText = `Hace ${minutes} minuto${minutes !== 1 ? "s" : ""}`;
  } else {
    relativeText = "Hace un momento";
  }

  return {
    days,
    hours,
    minutes,
    totalHours,
    totalMinutes,
    relativeText,
    isRecent: totalHours < 1,
  };
};

/**
 * Formatea una fecha del servidor a string legible (normalizada)
 * @param {string|Date} serverDate - Fecha del servidor
 * @param {Object} options - Opciones de formato de Intl.DateTimeFormat
 * @returns {string} - Fecha formateada
 */
export const formatServerDate = (serverDate, options = {}) => {
  const date = normalizeServerDate(serverDate);
  if (!date) return "Fecha no disponible";

  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  };

  try {
    return date.toLocaleDateString("es-CL", defaultOptions);
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return "Fecha inválida";
  }
};

/**
 * Formatea una fecha del servidor a formato corto
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {string} - Fecha en formato corto (ej: "22 ene 2026")
 */
export const formatServerDateShort = (serverDate) => {
  return formatServerDate(serverDate, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: undefined,
    minute: undefined,
  });
};

/**
 * Formatea una fecha del servidor a formato con hora
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {string} - Fecha con hora (ej: "22 ene 2026, 14:30")
 */
export const formatServerDateTime = (serverDate) => {
  return formatServerDate(serverDate, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Calcula el estado del SLA basado en una fecha del servidor
 * @param {string|Date} serverDate - Fecha de creación
 * @param {number} slaHours - Horas límite del SLA (default: 24)
 * @returns {Object|null} - Estado del SLA
 */
export const calculateSLA = (serverDate, slaHours = 24) => {
  const elapsed = getTimeElapsed(serverDate);
  if (!elapsed) return null;

  const { totalHours } = elapsed;
  const percentage = Math.min((totalHours / slaHours) * 100, 100);
  const isBreached = totalHours > slaHours;
  const hoursRemaining = Math.max(slaHours - totalHours, 0);

  let status;
  let color;
  if (isBreached) {
    status = "breached";
    color = "error";
  } else if (percentage > 75) {
    status = "warning";
    color = "warning";
  } else if (percentage > 50) {
    status = "attention";
    color = "info";
  } else {
    status = "ok";
    color = "success";
  }

  return {
    percentage,
    isBreached,
    hoursRemaining,
    hoursElapsed: totalHours,
    status,
    color,
    label: isBreached
      ? `⚠️ Vencido (${Math.floor(totalHours - slaHours)}h de retraso)`
      : `${Math.round(totalHours)}h / ${slaHours}h`,
  };
};

/**
 * Calcula el nivel de urgencia basado en el tiempo transcurrido
 * @param {string|Date} serverDate - Fecha de creación
 * @param {string} currentStatus - Estado actual de la cotización
 * @returns {Object|null} - Nivel de urgencia
 */
export const calculateUrgency = (serverDate, currentStatus = "pending") => {
  // Si no está pendiente, no hay urgencia
  if (currentStatus !== "pending") {
    return {
      level: "none",
      color: "success",
      label: "Atendida",
      score: 0,
      requiresAction: false,
    };
  }

  const elapsed = getTimeElapsed(serverDate);
  if (!elapsed) return null;

  const { days, totalHours } = elapsed;

  let level, color, label, score;

  if (days >= 7) {
    level = "critical";
    color = "error";
    label = "¡Requiere atención urgente!";
    score = 100;
  } else if (days >= 3) {
    level = "high";
    color = "warning";
    label = "Atención requerida";
    score = 70;
  } else if (days >= 1) {
    level = "medium";
    color = "info";
    label = "Seguimiento sugerido";
    score = 40;
  } else if (totalHours >= 4) {
    level = "low";
    color = "success";
    label = "En tiempo";
    score = 20;
  } else {
    level = "new";
    color = "success";
    label = "Nueva";
    score = 10;
  }

  return {
    level,
    color,
    label,
    score,
    requiresAction: level === "critical" || level === "high",
    elapsed,
  };
};

/**
 * Convierte una fecha local a fecha del servidor (suma el offset)
 * @param {Date} localDate - Fecha local
 * @returns {Date|null}
 */
export const toServerDate = (localDate) => {
  if (!localDate) return null;
  const date = new Date(localDate);
  if (isNaN(date.getTime())) return null;
  date.setHours(date.getHours() + SERVER_OFFSET_HOURS);
  return date;
};

/**
 * Compara dos fechas del servidor (normalizadas)
 * @param {string|Date} date1 - Primera fecha
 * @param {string|Date} date2 - Segunda fecha
 * @returns {number} - -1 si date1 < date2, 0 si iguales, 1 si date1 > date2
 */
export const compareServerDates = (date1, date2) => {
  const d1 = normalizeServerDate(date1);
  const d2 = normalizeServerDate(date2);

  if (!d1 && !d2) return 0;
  if (!d1) return -1;
  if (!d2) return 1;

  const time1 = d1.getTime();
  const time2 = d2.getTime();

  if (time1 < time2) return -1;
  if (time1 > time2) return 1;
  return 0;
};

/**
 * Verifica si una fecha del servidor es de hoy
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {boolean}
 */
export const isToday = (serverDate) => {
  const date = normalizeServerDate(serverDate);
  if (!date) return false;

  const today = getNow();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Verifica si una fecha del servidor es de esta semana
 * @param {string|Date} serverDate - Fecha del servidor
 * @returns {boolean}
 */
export const isThisWeek = (serverDate) => {
  const date = normalizeServerDate(serverDate);
  if (!date) return false;

  const now = getNow();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);

  return date >= startOfWeek && date < endOfWeek;
};

export default {
  parseServerDate,
  normalizeServerDate,
  toServerDate,
  formatServerDate,
  formatServerDateShort,
  formatServerDateTime,
  getTimeElapsed,
  calculateSLA,
  calculateUrgency,
  compareServerDates,
  isToday,
  isThisWeek,
  getNow,
  SERVER_OFFSET_HOURS,
};
